import { defineStore } from 'pinia';
import { ref } from 'vue';
import { firstValueFrom } from 'rxjs';

import { OrderService } from '~/support/services';
import createBus from '~/support/utils/event-bus';

export const useOrdersStore = defineStore('orders', () => {
  const endDate = new Date();
  const startDate = new Date(new Date().setDate(endDate.getDate() - 7));
  const dateRange = ref([startDate, endDate]);
  const rowsPerPage = ref(10);
  const currentPage = ref(1);

  const orderCounts = ref(new Map<string, number>());
  const { orders: restSvc } = useGateway();

  const $bus = createBus();

  function setDateRange(range: any) {
    dateRange.value = range;
  }

  function setRowsPerPage(rows: number) {
    rowsPerPage.value = rows;
  }

  function setCurrentPage(page: number) {
    currentPage.value = page;
  }

  async function exportOrders(body: { storeIds: string[]; filter: string; timezone?: string }) {
    const service = new OrderService(useRuntimeConfig().public.apiBaseUrl);

    return service.exportOrders(body);
  }

  async function loadOrderCounts({ filters }: { filters: string }, force = false): Promise<number> {
    const cacheKey = JSON.stringify({ filters });

    if (!force && orderCounts.value.has(cacheKey)) {
      return orderCounts.value.get(cacheKey)!;
    }

    try {
      const response = await firstValueFrom(restSvc.orders.counts.forStores({ filters }));

      const count = response.data?.totalCount || 0;
      orderCounts.value.set(cacheKey, count);

      return count;
    } catch (error) {
      console.error('Error loading order counts:', error);

      return 0;
    }
  }

  function getOrderBySourceOrderId(email: string, sourceOrderId: string) {
    const service = new OrderService(useRuntimeConfig().public.apiBaseUrl);

    return service.findByEmailAndSourceOrderId(email, sourceOrderId);
  }

  return {
    $bus,
    currentPage,
    dateRange,
    exportOrders,
    getOrderBySourceOrderId,
    loadOrderCounts,
    orderCounts,
    rowsPerPage,
    setCurrentPage,
    setDateRange,
    setRowsPerPage,
  };
});
